<template>
  <ion-modal :is-open="isOpen" @didDismiss="$emit('didDismiss')" class="feature-modal">
    <div class="modal-content">
      <!-- Simple Carousel with dots instead of Swiper -->
      <div class="carousel">
        <div v-for="(slide, index) in slides" :key="index" 
             :class="['carousel-slide', { 'active': activeSlide === index }]">
          <img 
            :src="slide.image" 
            class="slide-image" 
            alt="Feature illustration" 
            :style="{ maxHeight: slide.imageMaxHeight || '300px' }"
          />
          <h2 class="slide-title">{{ slide.title }}</h2>
          <p class="slide-description">{{ slide.description }}</p>
        </div>
        
        <!-- Dots -->
        <div class="carousel-dots">
          <button 
            v-for="(slide, index) in slides" 
            :key="index"
            :class="['dot', { 'active': activeSlide === index }]"
            @click="goToSlide(index)"
            aria-label="Go to slide"
          ></button>
        </div>

        <!-- Navigation buttons -->
        <button @click="manualPrevSlide" class="nav-button prev-button" aria-label="Previous slide">&lt;</button>
        <button @click="manualNextSlide" class="nav-button next-button" aria-label="Next slide">&gt;</button>
      </div>
      
      <!-- Continue button -->
      <div class="modal-footer">
        <button @click="$emit('didDismiss')" class="continue-button">
          Понятно
        </button>
      </div>
    </div>
  </ion-modal>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted } from 'vue';
import { IonModal } from '@ionic/vue';
import voronahseCute from '@/assets/voronahse_cute.png'; // Import the image

export default defineComponent({
  name: 'FeatureShowcaseModal',
  components: {
    IonModal
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  emits: ['didDismiss'],
  setup() {
    // Active slide index
    const activeSlide = ref(0);
    const intervalId = ref<number | null>(null);
    const userInteracted = ref(false); // Flag to track user interaction
    
    // Define slides data with specific max heights
    const slides = ref([
      {
        title: 'Добро пожаловать!',
        description: 'Воронка поможет вам разобраться в учете индивидуальных достижений',
        image: voronahseCute,
        imageMaxHeight: '160px' // Smaller height for the mascot
      },
      {
        title: 'Поиск Достижений',
        description: 'Легко находите нужные достижения и олимпиады с помощью поиска',
        image: 'https://i.ibb.co/DfyrB7H8/2187112d-a38f-4a64-9c6d-ad044d2f259c-removalai-preview-2.png',
        imageMaxHeight: '300px' // Larger height for the screenshot
      },
      {
        title: 'Калькулятор Баллов',
        description: 'Рассчитайте свои баллы для конкретной образовательной программы легко и быстро. Для этого перейдите в раздел "Калькулятор по программам"',
        image: 'https://i.ibb.co/Hfqr8xK3/60b67534-3794-481c-9114-86640c5c0a6f-removalai-preview.png',
        imageMaxHeight: '300px' // Larger height for the screenshot
      }
    ]);
    
    // Function to clear the interval
    const stopAutoSlide = () => {
      if (intervalId.value) {
        clearInterval(intervalId.value);
        intervalId.value = null;
      }
    };
    
    // Methods for navigation
    const nextSlide = () => {
      if (!userInteracted.value) { // Check flag before proceeding
        activeSlide.value = (activeSlide.value + 1) % slides.value.length;
      }
    };
    
    const prevSlide = () => {
      if (!userInteracted.value) { // Check flag before proceeding
        activeSlide.value = (activeSlide.value - 1 + slides.value.length) % slides.value.length;
      }
    };

    const manualNextSlide = () => {
      userInteracted.value = true;
      stopAutoSlide();
      activeSlide.value = (activeSlide.value + 1) % slides.value.length;
    };

    const manualPrevSlide = () => {
      userInteracted.value = true;
      stopAutoSlide();
      activeSlide.value = (activeSlide.value - 1 + slides.value.length) % slides.value.length;
    };

    const goToSlide = (index: number) => {
      userInteracted.value = true;
      stopAutoSlide();
      activeSlide.value = index;
    };
    
    // Lifecycle hooks for auto-sliding
    onMounted(() => {
      stopAutoSlide(); // Clear any previous interval just in case
      intervalId.value = window.setInterval(() => {
        if (!userInteracted.value) { // Only advance if user hasn't interacted
          nextSlide();
        }
      }, 3500); // Slide every 2 seconds
    });

    onUnmounted(() => {
      stopAutoSlide(); // Clear interval on unmount
    });

    return {
      slides,
      activeSlide,
      manualNextSlide,
      manualPrevSlide,
      goToSlide
    };
  }
});
</script>

<style scoped>
.feature-modal {
  --height: auto;
  --width: 90%;
  --max-width: 500px;
  --border-radius: 16px;
  --box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  --backdrop-opacity: 0.5;
}

.modal-content {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}

.carousel {
  position: relative;
  overflow: hidden;
  padding: 2rem 1rem;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.carousel-slide {
  display: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 1rem;
  max-height: 450px;
  box-sizing: border-box;
}

.carousel-slide.active {
  display: flex;
  animation: fadeIn 0.5s;
}

.slide-image {
  max-width: 80%;
  margin-bottom: 1.5rem;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  object-fit: contain;
  border-radius: 8px;
}

.slide-title {
  font-size: 22px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.75rem;
}

.slide-description {
  font-size: 16px;
  color: #6b7280;
  line-height: 1.6;
  max-width: 90%;
  margin: 0 auto;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #d1d5db;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
}

.dot.active {
  background-color: #3b82f6;
  transform: scale(1.2);
}

.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(255, 255, 255, 0.8);
  border: 1px solid #e5e7eb;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;
}

.nav-button:hover {
  background-color: white;
  color: #3b82f6;
}

.prev-button {
  left: 1rem;
}

.next-button {
  right: 1rem;
}

.modal-footer {
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

.continue-button {
  width: 100%;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.continue-button:hover {
  background-color: #2563eb;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style> 